/**
 * Experiments API methods
 * Handles all experiment-related API calls
 */

import {
  extractIdFromLocation,
  getBasePath,
  normalizeTimestamp,
  createNeedsRefetchError
} from './base';
import type { Experiment } from './types';

import axiosInstance from '~/common/helpers/axiosInstance';

/**
 * Create an experiment in a dataset
 * Note: API uses 'name' not 'label'
 */
export const createExperiment = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  data: {
    name: string;
    variables: Record<string, string>;
    enabled?: boolean;
  }
): Promise<Experiment> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/experiments`;
  const response = await axiosInstance.post<Experiment>(path, data);

  const responseData: unknown = response.data;
  const isEmptyString =
    typeof responseData === 'string' && responseData.trim() === '';
  const experimentFromBody =
    responseData &&
    typeof responseData === 'object' &&
    !Array.isArray(responseData) &&
    !isEmptyString
      ? (responseData as Experiment)
      : null;

  if (experimentFromBody?.id) {
    return {
      ...experimentFromBody,
      createdAt: normalizeTimestamp(experimentFromBody.createdAt),
      updatedAt: normalizeTimestamp(experimentFromBody.updatedAt)
    };
  }

  const experimentId = extractIdFromLocation(
    response.headers?.location,
    'experiments'
  );

  if (!experimentId) {
    throw createNeedsRefetchError(
      'Experiment created but ID not available in response. Please refetch datasets.',
      data.name
    );
  }

  const now = Date.now();
  return {
    id: experimentId,
    datasetId,
    name: experimentFromBody?.name ?? data.name,
    variables: experimentFromBody?.variables ?? data.variables ?? {},
    enabled: experimentFromBody?.enabled ?? data.enabled ?? true,
    createdAt: normalizeTimestamp(experimentFromBody?.createdAt) || now,
    updatedAt: normalizeTimestamp(experimentFromBody?.updatedAt) || now
  };
};

/**
 * Update experiment
 * Note: API uses 'name' not 'label'
 */
export const updateExperiment = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  experimentId: string,
  data: {
    name?: string;
    variables?: Record<string, string>;
    enabled?: boolean;
  }
): Promise<Experiment> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}`;
  const response = await axiosInstance.patch<Experiment>(path, data);

  // Normalize timestamps
  const experiment = response.data;
  return {
    ...experiment,
    createdAt: normalizeTimestamp(experiment.createdAt),
    updatedAt: normalizeTimestamp(experiment.updatedAt)
  };
};

/**
 * Enable experiment
 */
export const enableExperiment = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  experimentId: string
): Promise<Experiment> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/enable`;
  const response = await axiosInstance.patch<Experiment>(path);

  // Normalize timestamps
  const experiment = response.data;
  return {
    ...experiment,
    createdAt: normalizeTimestamp(experiment.createdAt),
    updatedAt: normalizeTimestamp(experiment.updatedAt)
  };
};

/**
 * Disable experiment
 */
export const disableExperiment = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  experimentId: string
): Promise<Experiment> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/disable`;
  const response = await axiosInstance.patch<Experiment>(path);

  // Normalize timestamps
  const experiment = response.data;
  return {
    ...experiment,
    createdAt: normalizeTimestamp(experiment.createdAt),
    updatedAt: normalizeTimestamp(experiment.updatedAt)
  };
};

/**
 * Delete experiment
 */
export const deleteExperiment = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  experimentId: string
): Promise<void> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}`;
  await axiosInstance.delete(path);
};

/**
 * Execute experiments
 * Note: API endpoint is /run-experiments (not /datasets/:datasetId/run-experiments)
 */
export const runExperiments = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string | null,
  config?: {
    experimentIds?: string[] | null;
    testCase?: unknown; // Optional testCase with flowSpec, etc.
    connectorVersion?: number | string;
  }
): Promise<{ executionId: string }> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/run-experiments`;
  const payload: Record<string, unknown> = {};
  if (config?.testCase !== undefined && config?.testCase !== null) {
    payload.testCase = config.testCase;
  }

  // Only include datasetId and experimentIds if they are provided (not null/undefined)
  // For single runs, these should be omitted
  if (datasetId !== null && datasetId !== undefined) {
    payload.datasetId = datasetId;
  }
  if (config?.experimentIds !== null && config?.experimentIds !== undefined) {
    payload.experimentIds = config.experimentIds;
  }
  // Include connectorVersion only if provided (not null/undefined)
  // Don't send connectorVersion if versioning is not enabled or no version is selected
  if (config?.connectorVersion != null) {
    payload.connectorVersion = config.connectorVersion;
  }

  const response = await axiosInstance.post<{ executionId: string }>(
    path,
    payload
  );
  return response.data;
};
